import { useState } from 'react'
import { ArrowLeftRight, BarChart3, Boxes, CreditCard, FileSignature, FileText, Layers, Receipt, Settings2 } from 'lucide-react'
import {
  BetaBadge,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import { OverviewSection } from './sections/OverviewSection.tsx'
import { ContractListSection } from './sections/ContractListSection.tsx'
import { ContractGroupsSection } from './sections/ContractGroupsSection.tsx'
import { ProductCatalogAdminSection } from './sections/ProductCatalogAdminSection.tsx'
import { SollIstSection } from './sections/SollIstSection.tsx'
import { BillingSection } from './sections/BillingSection.tsx'
import { TemplatesSection } from './sections/TemplatesSection.tsx'
import { ReportingSection } from './sections/ReportingSection.tsx'
import { CustomFieldsSection } from './sections/CustomFieldsSection.tsx'

type SubPage = 'overview' | 'contracts' | 'catalog' | 'groups' | 'templates' | 'deviations' | 'billing' | 'reporting' | 'custom-fields'

export type VertraegeContext = 'sales' | 'purchase' | 'finance'

interface VertraegePageProps {
  /**
   * Determines the default view and which tabs are visible.
   * - sales: outbound contracts only, compact tabs (Overview/Contracts/Reporting)
   * - purchase: inbound contracts only, same tabs
   * - finance: all contracts, all tabs (incl. Product Catalog, Templates, Soll/Ist, Billing)
   * Default: 'finance'
   */
  context?: VertraegeContext
}

const CONTEXT_CONFIG: Record<
  VertraegeContext,
  {
    title: string
    description: string
    defaultTab: SubPage
    visibleTabs: ReadonlyArray<SubPage>
    defaultDirection: 'outbound' | 'inbound' | undefined
    listEmptyHint: string
  }
> = {
  sales: {
    title: 'Verkauf · Verträge',
    description: 'Kundenverträge, Tarife und Vorlagen aus Vertriebssicht.',
    defaultTab: 'contracts',
    visibleTabs: ['overview', 'contracts', 'templates'],
    defaultDirection: 'outbound',
    listEmptyHint: 'Kundenverträge anlegen und nachverfolgen.',
  },
  purchase: {
    title: 'Beschaffung · Verträge',
    description: 'Lieferantenverträge, Bezugspools und Reporting aus Einkaufssicht.',
    defaultTab: 'contracts',
    visibleTabs: ['overview', 'contracts', 'reporting'],
    defaultDirection: 'inbound',
    listEmptyHint: 'Lieferantenverträge anlegen und nachverfolgen.',
  },
  finance: {
    title: 'Verträge & Abrechnung',
    description: 'Alle Verträge inklusive Tarife, Reconciliation, Abrechnung und Generator.',
    defaultTab: 'overview',
    visibleTabs: ['overview', 'contracts', 'catalog', 'groups', 'templates', 'deviations', 'billing', 'reporting', 'custom-fields'],
    defaultDirection: undefined,
    listEmptyHint: 'Lege deinen ersten Vertrag an.',
  },
}

export function VertraegePage({ context = 'finance' }: VertraegePageProps = {}) {
  const { isBeta } = useModuleStatus('vertraege')
  const cfg = CONTEXT_CONFIG[context]
  const [subPage, setSubPage] = useState<SubPage>(cfg.defaultTab)

  const showTab = (tab: SubPage) => cfg.visibleTabs.includes(tab)

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-brand-500" />
            {cfg.title}
            {isBeta ? <BetaBadge size="md" /> : null}
          </span>
        }
        description={cfg.description}
      />

      <Tabs value={subPage} onValueChange={(v) => setSubPage(v as SubPage)}>
        <TabsList>
          {showTab('overview') && (
            <TabsTrigger value="overview">
              <CreditCard className="w-4 h-4" />
              Übersicht
            </TabsTrigger>
          )}
          {showTab('contracts') && (
            <TabsTrigger value="contracts">
              <FileText className="w-4 h-4" />
              Verträge
            </TabsTrigger>
          )}
          {showTab('catalog') && (
            <TabsTrigger value="catalog">
              <Boxes className="w-4 h-4" />
              Produktkatalog
            </TabsTrigger>
          )}
          {showTab('groups') && (
            <TabsTrigger value="groups">
              <Layers className="w-4 h-4" />
              Vertragsgruppen
            </TabsTrigger>
          )}
          {showTab('templates') && (
            <TabsTrigger value="templates">
              <FileSignature className="w-4 h-4" />
              Vorlagen
            </TabsTrigger>
          )}
          {showTab('deviations') && (
            <TabsTrigger value="deviations">
              <ArrowLeftRight className="w-4 h-4" />
              Soll/Ist
            </TabsTrigger>
          )}
          {showTab('billing') && (
            <TabsTrigger value="billing">
              <Receipt className="w-4 h-4" />
              Abrechnung
            </TabsTrigger>
          )}
          {showTab('reporting') && (
            <TabsTrigger value="reporting">
              <BarChart3 className="w-4 h-4" />
              Reporting
            </TabsTrigger>
          )}
          {showTab('custom-fields') && (
            <TabsTrigger value="custom-fields">
              <Settings2 className="w-4 h-4" />
              Zusatzfelder
            </TabsTrigger>
          )}
        </TabsList>

        {showTab('overview') && (
          <TabsContent value="overview">
            <OverviewSection />
          </TabsContent>
        )}
        {showTab('contracts') && (
          <TabsContent value="contracts">
            <ContractListSection
              defaultDirection={cfg.defaultDirection}
              emptyHint={cfg.listEmptyHint}
            />
          </TabsContent>
        )}
        {showTab('catalog') && (
          <TabsContent value="catalog">
            <ProductCatalogAdminSection />
          </TabsContent>
        )}
        {showTab('groups') && (
          <TabsContent value="groups">
            <ContractGroupsSection />
          </TabsContent>
        )}
        {showTab('templates') && (
          <TabsContent value="templates">
            <TemplatesSection />
          </TabsContent>
        )}
        {showTab('deviations') && (
          <TabsContent value="deviations">
            <SollIstSection />
          </TabsContent>
        )}
        {showTab('billing') && (
          <TabsContent value="billing">
            <BillingSection />
          </TabsContent>
        )}
        {showTab('reporting') && (
          <TabsContent value="reporting">
            <ReportingSection />
          </TabsContent>
        )}
        {showTab('custom-fields') && (
          <TabsContent value="custom-fields">
            <CustomFieldsSection />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
